import { useState, useEffect, useRef, type ChangeEvent, type FormEvent, type SyntheticEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Grow,
  Paper,
  Snackbar,
  TextField as MuiTextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import PersonIcon from '@mui/icons-material/Person';
import NotesIcon from '@mui/icons-material/Notes';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import DatePicker from '../../components/DatePicker';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import TextField from '../../components/TextField';
import PhoneTextField from '../../components/PhoneTextField';
import { isValidPhone } from '../../../shared/phoneInput';
import {
  appendQuickEntryUserToUrl,
  getStoredQuickEntryUser,
  MEDALLION_USERS,
  normalizeQuickEntryUser,
  setStoredQuickEntryUser,
} from '../../utils/quickEntryUser';
import { CAREGIVER_COUNTY_OPTIONS } from '../../config/picklistConfig';

const CAREGIVER_TYPES = ['HHA', 'CNA', 'RN', 'LPN', 'PT', 'PTA', 'OT', 'OTA', 'SLP'] as const;

const PRIMARY_LANGUAGES = [
  'English', 'Spanish', 'Albanian', 'Arabic', 'Bengali', 'Chinese', 'Creole',
  'Farsi', 'French', 'German', 'Gujarati', 'Hebrew', 'Hindi', 'Italian',
  'Japanese', 'Korean', 'Mandarin', 'Pashto', 'Polish', 'Portuguese',
  'Punjabi', 'Russian', 'Tagalog', 'Thai', 'Turkish', 'Ukrainian', 'Urdu', 'Yoruba',
] as const;

type LeadType = 'Patient' | 'Caregiver';

interface FormState {
  leadType: LeadType;
  firstName: string;
  lastName: string;
  mobilePhone: string;
  phone: string;
  dateOfBirth: string;
  medicaidId: string;
  street: string;
  aptSuite: string;
  city: string;
  state: string;
  zipCode: string;
  medallionNotes: string;
  medallionHealthUser: string;
  type: string;
  county: string;
  email: string;
  primaryLanguage: string;
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

const EMPTY_FORM: FormState = {
  leadType: 'Patient',
  firstName: '',
  lastName: '',
  mobilePhone: '',
  phone: '',
  dateOfBirth: '',
  medicaidId: '',
  street: '',
  aptSuite: '',
  city: '',
  state: '',
  zipCode: '',
  medallionNotes: '',
  medallionHealthUser: '',
  type: '',
  county: '',
  email: '',
  primaryLanguage: '',
};

function normalizeStateValue(value: string): string {
  return value.trim().toUpperCase();
}

function normalizeMedicaidId(value: string): string {
  return value.trim().toUpperCase();
}

function normalizeQuickEntryForm(form: FormState): FormState {
  return {
    ...form,
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    mobilePhone: form.mobilePhone.trim(),
    phone: form.phone.trim(),
    dateOfBirth: form.dateOfBirth.trim(),
    medicaidId: normalizeMedicaidId(form.medicaidId),
    street: form.street.trim(),
    aptSuite: form.aptSuite.trim(),
    city: form.city.trim(),
    state: normalizeStateValue(form.state),
    zipCode: form.zipCode.trim(),
    medallionNotes: form.medallionNotes.trim(),
    medallionHealthUser: form.medallionHealthUser.trim(),
    email: form.email.trim(),
  };
}

export default function QuickEntry() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const publicId = searchParams.get('id');
  const { isAuthenticated, isLoading, hasRole, authFetch, login, user } = useAuth();
  const normalizedPublicId = publicId?.trim() || '';
  const currentQeId = user?.username?.startsWith('qe_') ? user.username.slice(3) : null;
  const isCurrentPublicSession = !!normalizedPublicId && currentQeId === normalizedPublicId;
  const isPublic = !!normalizedPublicId && !isLoading && !isCurrentPublicSession;

  const newYorkPortalUrl = currentQeId
    ? `https://portal.linkhomecare.com/start?qe=true&id=${encodeURIComponent(currentQeId)}`
    : 'https://portal.linkhomecare.com/start?qe=true';

  const hasUserParam = searchParams.has('user');
  const userParam = searchParams.get('user');
  const matchedMedallionUser = hasUserParam ? normalizeQuickEntryUser(userParam) : getStoredQuickEntryUser();
  const showMedallionDropdown = !hasUserParam || !matchedMedallionUser;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [addressKey, setAddressKey] = useState(0);
  const autoLoginStartedRef = useRef<string | null>(null);

  const [publicLinkLoading, setPublicLinkLoading] = useState(!!publicId);
  const [publicLinkValid, setPublicLinkValid] = useState<boolean | null>(isPublic ? null : true);
  const [publicLinkError, setPublicLinkError] = useState('');

  const isCaregiver = form.leadType === 'Caregiver';
  const effectiveQuickEntryUser = normalizeQuickEntryUser(form.medallionHealthUser) || matchedMedallionUser;

  useEffect(() => {
    if (hasUserParam) {
      setStoredQuickEntryUser(userParam);
    }
  }, [hasUserParam, userParam]);

  useEffect(() => {
    if (!normalizedPublicId) {
      setPublicLinkLoading(false);
      return;
    }
    if (isLoading) return;
    if (isCurrentPublicSession) {
      setPublicLinkLoading(false);
      setPublicLinkValid(true);
      setPublicLinkError('');
      return;
    }
    if (autoLoginStartedRef.current === normalizedPublicId) return;

    let cancelled = false;
    autoLoginStartedRef.current = normalizedPublicId;
    setPublicLinkLoading(true);
    setPublicLinkError('');

    fetch('/api/auth/qe-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ qeLoginId: normalizedPublicId }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setPublicLinkError(data.message || t('qe.form.formNotAccessible'));
          setPublicLinkValid(false);
        } else {
          login(data.accessToken, data.refreshToken, data.user);
          setPublicLinkValid(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublicLinkError(t('qe.form.formNotAccessible'));
          setPublicLinkValid(false);
        }
      })
      .finally(() => {
        if (!cancelled) setPublicLinkLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCurrentPublicSession, isLoading, login, normalizedPublicId, t]);

  useEffect(() => {
    if (matchedMedallionUser) {
      setForm((prev) => ({ ...prev, medallionHealthUser: matchedMedallionUser }));
    }
  }, [matchedMedallionUser]);

  const updateField = (name: keyof FormState, value: string) => {
    const nextValue = name === 'state'
      ? normalizeStateValue(value)
      : name === 'medicaidId'
        ? normalizeMedicaidId(value)
        : value;
    if (name === 'medallionHealthUser') {
      setStoredQuickEntryUser(nextValue);
    }
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => {
      const nextErrors = { ...prev, [name]: '' };
      if ((name === 'mobilePhone' || name === 'phone') && isValidPhone(nextValue)) {
        const sibling = name === 'mobilePhone' ? 'phone' : 'mobilePhone';
        if (prev[sibling] === t('qe.validation.enterMobileOrPhone')) {
          nextErrors[sibling] = '';
        }
      }
      return nextErrors;
    });
  };

  const handleFieldChange =
    (name: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
      updateField(name, event.target.value);
    };

  const handleLeadTypeChange = (_event: React.MouseEvent<HTMLElement>, newValue: LeadType | null) => {
    if (!newValue) return;
    setForm({ ...EMPTY_FORM, leadType: newValue, medallionHealthUser: effectiveQuickEntryUser });
    setErrors({});
    setAddressKey((k) => k + 1);
  };

  const validate = (nextForm: FormState = form): boolean => {
    const newErrors: FieldErrors = {};
    if (!nextForm.firstName.trim()) newErrors.firstName = t('qe.validation.required');
    if (!nextForm.lastName.trim()) newErrors.lastName = t('qe.validation.required');
    if (!nextForm.mobilePhone.trim() && !nextForm.phone.trim()) {
      newErrors.mobilePhone = t('qe.validation.enterMobileOrPhone');
      newErrors.phone = t('qe.validation.enterMobileOrPhone');
    }
    for (const field of ['mobilePhone', 'phone'] as const) {
      if (nextForm[field].trim() && !isValidPhone(nextForm[field])) {
        newErrors[field] = t('qe.validation.phoneDigits');
      }
    }
    const stateVal = normalizeStateValue(nextForm.state);
    if (stateVal && !/^[A-Za-z]{2}$/.test(stateVal)) {
      newErrors.state = t('qe.validation.stateCode');
    }
    if (nextForm.leadType === 'Caregiver') {
      if (!nextForm.type) newErrors.type = t('qe.validation.required');
      if (!nextForm.county) newErrors.county = t('qe.validation.required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSwitchToNewYork = () => {
    const destinationUrl = isPublic
      ? `https://portal.linkhomecare.com/quick-entry${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
      : newYorkPortalUrl;
    window.open(appendQuickEntryUserToUrl(destinationUrl, effectiveQuickEntryUser), '_blank', 'noopener,noreferrer');
  };

  const handleCloseSuccessToast = (_event?: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSuccessOpen(false);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedForm = normalizeQuickEntryForm(form);
    setForm(normalizedForm);
    if (!validate(normalizedForm)) return;
    if (isPublic && (publicLinkLoading || !!publicLinkError)) return;
    setSubmitting(true);
    setErrorMsg('');
    try {
      let res: Response;
      if (isPublic) {
        res = await fetch(`/api/quick-entry/public?id=${encodeURIComponent(normalizedPublicId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(normalizedForm),
        });
      } else {
        res = await authFetch('/api/quick-entry', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(normalizedForm),
        });
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.message || t('qe.form.failedToCreate'));
      } else {
        setSuccessOpen(true);
        setForm({ ...EMPTY_FORM, leadType: form.leadType, medallionHealthUser: effectiveQuickEntryUser });
        setErrors({});
        setAddressKey((k) => k + 1);
      }
    } catch {
      setErrorMsg(t('qe.form.networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isPublic && publicLinkLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isPublic && publicLinkValid === false) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Grow in style={{ transformOrigin: '50% 50%' }} timeout={800}>
          <Paper
            elevation={6}
            sx={{
              p: { xs: 3, sm: 5 },
              textAlign: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}
            >
              <CancelOutlinedIcon sx={{ fontSize: 64, color: '#8900e1' }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {t('qe.form.oops')}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {publicLinkError || t('qe.form.formNotAccessible')}
              </Typography>
            </Box>
          </Paper>
        </Grow>
      </Container>
    );
  }

  if (!isPublic && isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isPublic && !isAuthenticated) {
    navigate('/start');
    return null;
  }

  if (!isPublic && isAuthenticated && !hasRole('quick-entry') && !hasRole('admin')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography variant="h6" color="error">
          {t('qe.form.accessDenied')}
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <Paper elevation={3} sx={{ p: 2, mb: 10, bgcolor: 'rgba(255,255,255,1.00)' }}>
        <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
          {t('qe.form.newLead')}
        </Typography>

        <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
          <ToggleButtonGroup
            value={form.leadType}
            exclusive
            onChange={handleLeadTypeChange}
            size="small"
          >
            <ToggleButton
              value="Patient"
              sx={{
                px: 3,
                textTransform: 'none',
                '&.Mui-selected': { backgroundColor: '#8600e1', color: '#fff', '&:hover': { backgroundColor: '#7005c1' } },
              }}
            >
              {t('qe.form.patient')}
            </ToggleButton>
            <ToggleButton
              value="Caregiver"
              sx={{
                px: 3,
                textTransform: 'none',
                '&.Mui-selected': { backgroundColor: '#8600e1', color: '#fff', '&:hover': { backgroundColor: '#7005c1' } },
              }}
            >
              {t('qe.form.caregiver')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <form onSubmit={handleSubmit}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ mr: 1, color: '#8600e1' }} />
                <Typography variant="h6">
                  {isCaregiver ? t('qe.form.caregiverDetails') : t('qe.form.patientDetails')}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={isCaregiver ? t('qe.form.firstName') : t('qe.form.patientFirstName')}
                    fullWidth
                    required
                    value={form.firstName}
                    error={!!errors.firstName}
                    helperText={errors.firstName || ''}
                    onChange={handleFieldChange('firstName')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={isCaregiver ? t('qe.form.lastName') : t('qe.form.patientLastName')}
                    fullWidth
                    required
                    value={form.lastName}
                    error={!!errors.lastName}
                    helperText={errors.lastName || ''}
                    onChange={handleFieldChange('lastName')}
                  />
                </Grid>

                {isCaregiver && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <MuiTextField
                        select
                        label={t('qe.form.caregiverType')}
                        fullWidth
                        required
                        value={form.type}
                        error={!!errors.type}
                        helperText={errors.type || ''}
                        onChange={(e) => updateField('type', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>{t('qe.form.selectType')}</em>
                        </MenuItem>
                        {CAREGIVER_TYPES.map((ctype) => (
                          <MenuItem key={ctype} value={ctype}>{ctype}</MenuItem>
                        ))}
                      </MuiTextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MuiTextField
                        select
                        label={t('qe.form.county')}
                        fullWidth
                        required
                        value={form.county}
                        error={!!errors.county}
                        helperText={errors.county || ''}
                        onChange={(e) => updateField('county', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>{t('qe.form.selectCounty')}</em>
                        </MenuItem>
                        {CAREGIVER_COUNTY_OPTIONS.map((c) => (
                          <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                      </MuiTextField>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <PhoneTextField
                    key={form.leadType}
                    label={isCaregiver ? t('qe.form.mobile') : t('qe.form.patientMobile')}
                    fullWidth
                    value={form.mobilePhone}
                    onValueChange={(value) => updateField('mobilePhone', value)}
                    invalidPhoneMessage={t('qe.validation.phoneDigits')}
                    inputLimitMessage={t('qe.validation.phoneInputLimit')}
                    error={!!errors.mobilePhone}
                    helperText={errors.mobilePhone || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label={isCaregiver ? t('qe.form.dateOfBirth') : t('qe.form.patientDateOfBirth')}
                    name="dateOfBirth"
                    value={form.dateOfBirth}
                    onChange={(e) => updateField('dateOfBirth', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneTextField
                    key={form.leadType}
                    label={isCaregiver ? t('qe.form.phone') : t('qe.form.patientPhone')}
                    fullWidth
                    value={form.phone}
                    onValueChange={(value) => updateField('phone', value)}
                    invalidPhoneMessage={t('qe.validation.phoneDigits')}
                    inputLimitMessage={t('qe.validation.phoneInputLimit')}
                    error={!!errors.phone}
                    helperText={errors.phone || ''}
                  />
                </Grid>

                {isCaregiver ? (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('qe.form.email')}
                      fullWidth
                      value={form.email}
                      onChange={handleFieldChange('email')}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('qe.form.medicaidId')}
                      inputMask="aa99999a"
                      maskChar={null}
                      fullWidth
                      value={form.medicaidId}
                      onChange={handleFieldChange('medicaidId')}
                    />
                  </Grid>
                )}

                {isCaregiver && (
                  <Grid item xs={12} sm={6}>
                    <MuiTextField
                      select
                      label={t('qe.form.primaryLanguage')}
                      fullWidth
                      value={form.primaryLanguage}
                      onChange={(e) => updateField('primaryLanguage', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('qe.form.none')}</em>
                      </MenuItem>
                      {PRIMARY_LANGUAGES.map((lang) => (
                        <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                      ))}
                    </MuiTextField>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <AddressAutocomplete
                    key={addressKey}
                    onAddressSelect={({ street, aptSuite, city, state, zipCode }) => {
                      setForm((prev) => ({
                        ...prev,
                        street,
                        aptSuite,
                        city,
                        state: normalizeStateValue(state),
                        zipCode,
                      }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('qe.form.street')}
                    fullWidth
                    value={form.street}
                    onChange={handleFieldChange('street')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('qe.form.aptSuite')}
                    fullWidth
                    value={form.aptSuite}
                    onChange={handleFieldChange('aptSuite')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('qe.form.city')}
                    fullWidth
                    value={form.city}
                    onChange={handleFieldChange('city')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('qe.form.state')}
                    inputMask="aa"
                    maskChar={null}
                    fullWidth
                    value={form.state}
                    onChange={handleFieldChange('state')}
                    error={!!errors.state}
                    helperText={errors.state || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('qe.form.postalCode')}
                    fullWidth
                    value={form.zipCode}
                    onChange={handleFieldChange('zipCode')}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {showMedallionDropdown && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <MuiTextField
                  select
                  label={t('qe.form.createdBy')}
                  fullWidth
                  value={form.medallionHealthUser}
                  onChange={(e) => updateField('medallionHealthUser', e.target.value)}
                >
                  <MenuItem value="">
                    <em>{t('qe.form.none')}</em>
                  </MenuItem>
                  {MEDALLION_USERS.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </MuiTextField>
              </CardContent>
            </Card>
          )}

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <NotesIcon sx={{ mr: 1, color: '#8600e1' }} />
                <Typography variant="h6">{t('qe.form.notes')}</Typography>
              </Box>
              <TextField
                label={t('qe.form.additionalInfo')}
                fullWidth
                multiline
                minRows={4}
                value={form.medallionNotes}
                onChange={handleFieldChange('medallionNotes')}
              />
            </CardContent>
          </Card>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" gap={1}>
              {!isPublic && (
                <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={() => navigate('/qe-landing')}>
                  {t('qe.form.backToLanding')}
                </Button>
              )}
              <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={handleSwitchToNewYork}>
                {t('qe.switchToNewYork')}
              </Button>
            </Box>
            <Button
              type="submit"
              variant="contained"
              sx={{ textTransform: 'none' }}
              disabled={submitting || (isPublic && (publicLinkLoading || !!publicLinkError))}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : t('qe.form.submitLead')}
            </Button>
          </Box>
        </form>

        <Snackbar
          open={successOpen}
          autoHideDuration={4000}
          onClose={handleCloseSuccessToast}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSuccessToast} severity="success" sx={{ width: '100%' }}>
            {t('qe.form.leadCreated')}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}
